import React from 'react'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'
import { faPen } from '@fortawesome/pro-regular-svg-icons'

import { Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import {
  formatThousands,
  incomeAmountFieldName,
  INTAKE_QUESTION_CARD_CLS,
  isNotApplicableAnswer,
  isUnknownAnswer,
  stateName,
} from '../lib/constants'
import type { QuestionDef } from '../questions/types'

const SKIPPED = 'Skipped'
const UNKNOWN = "I don't know"
const NOT_APPLICABLE = "I don't have an entity"

/** The date fields can only record a year plus an optional month, and a
 *  year-only answer is stored with the month defaulted to January — on the
 *  wire that's indistinguishable from a deliberate January. Summarising it
 *  as "Jan 2027" would assert a month the user may never have picked, so
 *  these rows show only the part the field actually guarantees. */
const formatDateValue = (value: string | null | undefined): string => {
  if (isUnknownAnswer(value)) return UNKNOWN
  if (isNotApplicableAnswer(value)) return NOT_APPLICABLE
  return value
    ? DateTime.fromISO(value).toFormat(DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT)
    : SKIPPED
}

const optionLabel = (def: QuestionDef, value: string): string =>
  def.options?.find((o) => o.value === value)?.label ?? value

const asArray = (value: unknown): string[] =>
  Array.isArray(value) ? (value as string[]) : []

/** An optional per-state question can come back as `null` (explicitly
 *  skipped) or `{}` (never filled in) as well as a real map — treat all
 *  three as valid rather than assuming the map. */
const perStateEntries = (value: unknown): [string, unknown][] =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value as Record<string, unknown>)
    : []

/** Turns a raw stored answer back into the short line shown on a collapsed
 *  question row — one branch per `InputType`, since each shape (plain
 *  value, array, per-state record) needs its own formatting. */
const formatAnswerSummary = (def: QuestionDef, value: unknown): string => {
  switch (def.input) {
    case 'single':
    case 'income_band_slider':
      return optionLabel(def, value as string)
    case 'multi':
      return (
        asArray(value)
          .map((v) => optionLabel(def, v))
          .join(', ') || SKIPPED
      )
    case 'states':
      return asArray(value).map(stateName).join(', ') || SKIPPED
    case 'state':
      return value ? stateName(value as string) : SKIPPED
    case 'date':
      return formatDateValue(value as string | null | undefined)
    case 'per_state_date':
      return (
        perStateEntries(value)
          .map(
            ([state, date]) =>
              `${state}: ${formatDateValue(date as string | null | undefined)}`
          )
          .join(' · ') || SKIPPED
      )
    case 'per_state_select':
      return (
        perStateEntries(value)
          .map(([state, credential]) => `${state}: ${credential}`)
          .join(' · ') || SKIPPED
      )
    case 'number':
      return `${value}${def.unit ? ` ${def.unit}` : ''}`
    default:
      return String(value)
  }
}

/**
 * The rolled-up view of an already-answered question — one line instead of
 * its full control, so a section reads as a shrinking task list rather than
 * a wall of controls once you've moved past something. Click anywhere to
 * pop it back open for editing.
 */
export const CollapsedQuestionRow: React.FC<{
  def: QuestionDef
  value: unknown
  onExpand: () => void
}> = ({ def, value, onExpand }) => {
  // Read, not register — this runs for every question, and only the income
  // slider has a companion amount field to find.
  const { values } = useFormikContext<Record<string, unknown>>()
  const exactAmount = values[incomeAmountFieldName(def.key)]
  const summary =
    def.input === 'income_band_slider' && typeof exactAmount === 'number'
      ? formatThousands(exactAmount)
      : formatAnswerSummary(def, value)
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={false}
      className={mtc(
        INTAKE_QUESTION_CARD_CLS,
        'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:border-neutral-300 hover:bg-neutral-25'
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {def.prompt}
        </Text>
        <Text variant={TypographyStyles.LABEL_SM} className="truncate">
          {summary}
        </Text>
      </div>
      <Icon
        icon={faPen}
        size="sm"
        color="text-neutral-400"
        className="shrink-0"
      />
    </button>
  )
}
